import React, { useMemo, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { isMatch } from 'lodash-es';

import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { DropdownMenuItemGroup } from 'ui-components/components/dropdown-menu-item-group';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { emptyArray } from 'ui-components/default-props/empty';

import { useShipFromContext } from '../../contexts/ship-from-context';

import { noneInventorySource, inventorySourceTypes } from './constants';
import InventorySourceDropdownToggler from './inventory-source-dropdown-toggler';
import InventorySourceName from './inventory-source-name';

import { buildInventorySourceGroupKey, buildInventorySourceKey } from './key-service';

import messages from '../messages';
import style from './style.module.less';

const elementId = 'ship-from-inventory-dropdown';

const getInventorySourceDropdownGroupLabel = (inventorySourceGroup, intl) => {
  if (inventorySourceGroup.inventorySourceType === inventorySourceTypes.native) {
    return intl.formatMessage(messages.shipstationInventory);
  }
  return inventorySourceGroup.warehouseGroupName;
};

const normalizeInventorySourceGroups = (inventorySourceGroups) => {
  return (
    inventorySourceGroups?.map((inventorySourceGroup) => {
      return {
        ...inventorySourceGroup,
        warehouses: inventorySourceGroup.warehouses.map((warehouse) => ({
          ...warehouse,
          inventorySourceType: inventorySourceGroup.inventorySourceType,
        })),
      };
    }) || emptyArray
  );
};

const InventorySourceDropdown = () => {
  const intl = useIntl();
  const { shipFrom, updateShipFrom, sources: inventorySourceGroupResults } = useShipFromContext();
  // oxlint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const aggregateShipFromInventoryData = {
    inventorySourceType: shipFrom.inventorySourceType,
    inventorySourceId: shipFrom.inventorySourceId,
    inventorySourceExternalId: shipFrom.inventorySourceExternalId,
  };

  const onClick = useCallback(
    (inventorySource) => {
      const { inventorySourceType, inventorySourceId, inventorySourceExternalId } = inventorySource;
      updateShipFrom({
        inventorySourceType,
        inventorySourceId,
        inventorySourceExternalId,
      });
    },
    [updateShipFrom],
  );

  const inventorySourceGroups = useMemo(() => {
    return normalizeInventorySourceGroups(inventorySourceGroupResults);
  }, [inventorySourceGroupResults]);

  const selectedInventorySource = useMemo(() => {
    return (
      inventorySourceGroups
        .flatMap((inventorySourceGroup) => inventorySourceGroup.warehouses)
        .find((inventorySource) => isMatch(inventorySource, aggregateShipFromInventoryData)) || noneInventorySource
    );
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- aggregateShipFromInventoryData changes every render
  }, [inventorySourceGroups, aggregateShipFromInventoryData]);

  return (
    <div>
      <label className={style['inventory-label']} htmlFor={elementId}>
        <FormattedMessage defaultMessage='Inventory Source' description='inventory source dropdown label on ship from modal' id='R6qGUH' />
      </label>
      <DropdownMenu className={style.dropdown} dropdownMenuShouldHaveWidthOfButton fullWidth id={elementId} variant='form'>
        <DropdownMenuToggler theme='white'>
          <InventorySourceDropdownToggler inventorySourceGroups={inventorySourceGroups} selectedInventorySource={selectedInventorySource} />
        </DropdownMenuToggler>
        <DropdownMenuItem
          className={style['dropdown-menu-item']}
          onClick={() => onClick(noneInventorySource)}
          selected={isMatch(noneInventorySource, aggregateShipFromInventoryData)}
        >
          <InventorySourceName inventorySource={noneInventorySource} />
        </DropdownMenuItem>
        {inventorySourceGroups
          ?.filter((sg) => !!sg?.warehouses.length)
          .map((inventorySourceGroup, index) => (
            <DropdownMenuItemGroup
              key={buildInventorySourceGroupKey(inventorySourceGroup)}
              index={index}
              label={getInventorySourceDropdownGroupLabel(inventorySourceGroup, intl)}
              theme='white'
            >
              {inventorySourceGroup.warehouses.map((inventorySource) => (
                <DropdownMenuItem
                  key={buildInventorySourceKey(inventorySource)}
                  className={style['dropdown-menu-item']}
                  onClick={() => onClick(inventorySource)}
                  selected={isMatch(inventorySource, shipFrom)}
                >
                  <InventorySourceName inventorySource={inventorySource} />
                </DropdownMenuItem>
              ))}
            </DropdownMenuItemGroup>
          ))}
      </DropdownMenu>
    </div>
  );
};

export default InventorySourceDropdown;
